import React, { useEffect } from 'react';
import { Button } from 'reactstrap';
import { Scrollbars } from 'react-custom-scrollbars-2';
import {
  thumbVerticalStyle,
  trackVerticalStyle,
} from '../../../constants/styleScrollbar';
import TermsConditions from '../../acceptance/TermsConditions';
import {
  ClientPortalNewPaymentDetailProps,
  SundhedClientPortalClientData,
  SundhedClientPortalCreditFacilityData,
} from 'components/interfaces';
import { calculateTotalToBePaidInInterest } from '../../../constants/utils';
import { strings } from '../../../constants/localization';

type AcceptNewLoanAgreementProps = {
  onNextStep: () => void;
  setIsLoading: (value: boolean) => void;
  handlePreviousStep: () => void;
  newPaymentDetails: {
    monthlyPayment: number;
    apr: number;
    firstPaymentDate: string;
    lastPaymentDate: string;
    duration: number;
  };
  serviceFee?: number;
  extraPayment?: string;
  setNewPaymentDetails: (
    details: ClientPortalNewPaymentDetailProps | undefined
  ) => void;
  agreementType?: string;
  finalInterestRate: number;
  clientDetails: SundhedClientPortalClientData | null;
  creditFacilityDetails: SundhedClientPortalCreditFacilityData | null;
};

const AcceptNewLoanAgreement: React.FC<AcceptNewLoanAgreementProps> = ({
  onNextStep,
  setIsLoading,
  newPaymentDetails,
  extraPayment,
  serviceFee,
  setNewPaymentDetails,
  handlePreviousStep,
  agreementType,
  finalInterestRate,
  clientDetails,
  creditFacilityDetails,
}) => {
  const { monthlyPayment, apr, firstPaymentDate, lastPaymentDate, duration } =
    newPaymentDetails;

  const { firstName, lastName, address, zipCode, city, cpr, CPR } =
    clientDetails || {};

  const {
    amount,
    number,
    newLoanOwnerName,
    newLoanOwnerNameExtended,
    remainingBalance,
    interestRate,
    brand
  } = creditFacilityDetails || {};

  const totalRepay = monthlyPayment * duration;

  const newCreditAmount = remainingBalance - parseFloat(extraPayment);

  const totalCreditCost = totalRepay - newCreditAmount;

  const newRemainingBalance = remainingBalance - parseFloat(extraPayment);

  // Calculate total interest to be paid
  const totalInterest = calculateTotalToBePaidInInterest(
    newRemainingBalance,
    finalInterestRate,
    firstPaymentDate
  );

  const remainingBalanceWithTotalInterest = newRemainingBalance + totalInterest;

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleNextStep = () => {
    setNewPaymentDetails({
      monthlyPayment,
      apr,
      firstPaymentDate,
      lastPaymentDate,
      duration,
      remainingBalance,
      extraPayment: parseFloat(extraPayment),
      newRemainingBalance: remainingBalanceWithTotalInterest,
      interestUntilFirstPayment: totalInterest,
    });
    onNextStep();
  };

  return (
    <>
      <div className='step-content acceptloanAgreement'>
        <h1 className='step-title center'>
          {strings.partialRedemptionAcceptLoanAgreementTitle}
        </h1>

        <div className='payment-info-container'>
          <Scrollbars
            // eslint-disable-next-line
            renderTrackVertical={({ style, ...props }) => (
              <div
                {...props}
                style={{
                  ...style,
                  ...trackVerticalStyle,
                }}
              />
            )}
            // eslint-disable-next-line
            renderThumbVertical={({ style, ...props }) => (
              <div
                {...props}
                style={{
                  ...style,
                  ...thumbVerticalStyle,
                }}
              />
            )}
          >
            <div className='scrollbar-wrapper'>
              <TermsConditions
                type={agreementType ?? 'invoice'}
                brand={brand}
                clientName={`${firstName} ${lastName}`}
                clientAddress={address}
                clientCity={city}
                clientZip={zipCode}
                loanNumber={number}
                loanAmount={amount}
                setupFee={serviceFee}
                duration={duration}
                interestRate={interestRate}
                apr={apr.toFixed(2)}
                cpr={cpr || CPR}
                totalCosts={totalCreditCost}
                totalRepayment={totalRepay}
                monthlyInstallment={monthlyPayment}
                creditFacilityNumber={number}
                loanOwnerNameExtended={newLoanOwnerNameExtended}
                loanOwnerName={newLoanOwnerName}
                firstPaymentDate={firstPaymentDate}
                lastPaymentDate={lastPaymentDate}
              />
            </div>
          </Scrollbars>
        </div>

        <div className='step-actions'>
          <Button color='light' className='reject' onClick={handlePreviousStep}>
            {strings.partialRedemptionButtonBack}
          </Button>
          <Button color='blue' className='continue' onClick={handleNextStep}>
            {strings.acceptStep}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AcceptNewLoanAgreement;
